/* lf-related-products — swaps Shopify's related-product recommendations into
   the server-rendered fallback row on the product page.

   The section renders the fallback collection first so the row is never empty,
   then this fetches the same section back through the Product Recommendations
   API (one intent per request, so: related) and replaces the row's items with
   the recommendations, backfilling from the fallback to reach the limit.
   Deduped by product id, current product excluded. Only the <ul>'s children
   are replaced — the section wrapper, heading and stylesheet stay put.

   Scroll container: Horizon's squeeze layout makes `.page-wrapper` the page
   scroller at >=990px and `document.scrollingElement` below it. The observer's
   root has to follow, or `.page-wrapper`'s clip trims the 400px prefetch margin
   away on desktop and the fetch only starts once the row is already on screen.
   Hence @theme/scroll-container, and the rebind when the 990px axis flips. */

import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

const ROOT = '[data-lf-related-products]';
const ROW = '[data-lf-rp-row]';

/** 400px of runway below the scrollport, so the row is populated before it lands. */
const ROOT_MARGIN = '0px 0px 400px 0px';

let observer = null;

const inDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

const itemsFrom = (html) => {
  if (!html || !html.trim()) return [];
  const row = new DOMParser().parseFromString(html, 'text/html').querySelector(ROW);
  return row ? Array.from(row.children) : [];
};

const load = async (root) => {
  if (root.dataset.lfRpLoaded) return;
  root.dataset.lfRpLoaded = '1';

  const { productId, sectionId, url, limit } = root.dataset;
  if (!productId || !sectionId || !url) return;

  const max = parseInt(limit, 10) || 4;
  const endpoint = `${url}&product_id=${encodeURIComponent(productId)}&section_id=${encodeURIComponent(
    sectionId
  )}&intent=related`;

  let recommended = [];
  try {
    const response = await fetch(endpoint);
    if (!response.ok) return;
    recommended = itemsFrom(await response.text());
  } catch (error) {
    return; // network error: leave the server-rendered fallback in place
  }
  if (!recommended.length) return;

  const row = root.querySelector(ROW);
  if (!row) return;

  const seen = new Set([String(productId)]);
  const merged = [];
  for (const node of [...recommended, ...Array.from(row.children)]) {
    const pid = node.getAttribute('data-pid');
    if (!pid || seen.has(pid)) continue;
    seen.add(pid);
    merged.push(node);
    if (merged.length >= max) break;
  }
  if (!merged.length) return;

  row.replaceChildren(...merged.map((node) => document.importNode(node, true)));
};

const onIntersect = (entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    observer?.unobserve(entry.target);
    load(entry.target);
  });
};

/** (Re)build the observer against whichever element is currently the scroller. */
const buildObserver = () => {
  observer?.disconnect();
  observer = new IntersectionObserver(onIntersect, {
    root: getIntersectionRoot(),
    rootMargin: ROOT_MARGIN,
  });
};

/** Watch every root that has not fetched yet. Safe to call repeatedly. */
const observeAll = () => {
  document.querySelectorAll(ROOT).forEach((root) => {
    if (root.dataset.lfRpLoaded) return;
    observer.observe(root);
  });
};

const init = () => {
  // Theme editor: the recommendations API returns nothing useful in the
  // preview, so keep the server-rendered fallback and skip the fetch.
  if (inDesignMode()) return;
  if (!document.querySelector(ROOT)) return;

  if (!observer) buildObserver();
  observeAll();
};

// The page scroller changed under us: re-point the observer, then re-watch the
// rows that are still waiting. A disconnect drops every registration, so this
// has to re-observe rather than just swap the root.
scrollContainerMediaQuery.addEventListener('change', () => {
  if (inDesignMode() || !observer) return;
  buildObserver();
  observeAll();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
